import {
  KNOWLEDGE_DOC_INTRA_DOC_SEARCH,
  useGeckoAnalytics,
} from 'features/analytics'
import { DashboardTabInfo } from 'features/knowledgeDocumentViewer/components/ContextMenu'
import { KnowledgeDocumentSidebar } from 'features/knowledgeDocumentViewer/components/sidebar/KnowledgeDocumentSidebar'
import { PageThumbnailSidebar } from 'features/knowledgeDocumentViewer/components/thumbnailSidebar/PageThumbnailSidebar'
import { DocumentViewer } from 'features/knowledgeDocumentViewer/components/viewer/DocumentViewer'
import { IntraDocSearchBar } from 'features/knowledgeDocumentViewer/components/viewer/search/IntraDocSearchBar'
import type { SearchHighlightData } from 'features/knowledgeDocumentViewer/components/viewer/search/SearchHighlightOverlay'
import {
  Box,
  DocumentViewerHandle,
  type FrameTarget,
} from 'features/knowledgeDocumentViewer/components/viewer/shared/types'
import { useDocumentTabDataContext } from 'features/knowledgeDocumentViewer/contexts/DocumentTabDataContext'
import { useInstrumentSelectionOptional } from 'features/knowledgeDocumentViewer/contexts/InstrumentSelectionContext'
import { useKnowledgeDocumentDataContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentDataContext'
import { useKnowledgeDocumentUXContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentUXContext'
import { useAddDocumentToRecents } from 'features/knowledgeDocumentViewer/hooks/useAddDocumentToRecents'
import { useBoxClickHandler } from 'features/knowledgeDocumentViewer/hooks/useBoxClickHandler'
import { useFragmentHighlight } from 'features/knowledgeDocumentViewer/hooks/useFragmentHighlight'
import {
  type IntraDocSearchHit,
  type IntraDocSearchUiState,
  useIntraDocumentSearch,
} from 'features/knowledgeDocumentViewer/hooks/useIntraDocumentSearch'
import {
  LinkedDocumentsNavigationOptions,
  useLinkedDocumentsNavigation,
} from 'features/knowledgeDocumentViewer/hooks/useLinkedDocumentsNavigation'
import { useOffPageConnectorDocuments } from 'features/knowledgeDocumentViewer/hooks/useOffPageConnectorDocuments'
import { usePageNavigation } from 'features/knowledgeDocumentViewer/hooks/usePageNavigation'
import type {
  FragmentHighlight,
  RegionHighlight,
} from 'features/knowledgeDocumentViewer/types'
import {
  FilterOption,
  TooltipOption,
} from 'features/knowledgeDocumentViewer/types'
import type { AliasHighlight } from 'features/knowledgeDocumentViewer/types/documentSubjects'
import {
  DocumentViewerAssetType,
  shouldForceHideDocumentBoxes,
} from 'features/knowledgeDocumentViewer/types/ocrViewer'
import {
  docReferenceResolutionTexts,
  isSelfReference,
  normalizeReferenceText,
  resolveDocReferenceTargets,
} from 'features/knowledgeDocumentViewer/utils/docReferenceResolver'
import {
  allCandidatesHaveRevisionCodes,
  getLatestCandidateByRevision,
  type NavigationTargetCandidate,
} from 'features/knowledgeDocumentViewer/utils/navigationTargetResolver'
import {
  getViewerPreferences,
  updateViewerPreferences,
} from 'features/knowledgeDocumentViewer/utils/viewerPreferencesStorage'
import { useGeckoNotifications } from 'features/notifications'
import { useGeckoPermissions } from 'features/permissions'
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { DocumentUnavailablePanel } from './DocumentUnavailablePanel'
import {
  DocumentContentRoot,
  SidebarContainer,
  ThumbnailSidebarContainer,
  ViewerAndSidebarContainer,
  ViewerContainer,
} from './knowledgeDocumentToolsStyles'
import { KnowledgeDocumentHeaderBar } from './toolbar/KnowledgeDocumentHeaderBar'

interface DocumentTabContentProps {
  documentId: string
  pageNumber: number
  selectedBoxIds: string[]
  selectedSubjectUid?: string
  scale: number
  isActiveTab: boolean
  hasTimeSeriesData: (instrumentText: string | undefined) => boolean
  getMatchedDatapoints: (
    instrumentText: string | undefined
  ) => Array<{ id: string }>
  setIsChatbotOpen: (open: boolean) => void
  onBoxContextMenuChange: (state: BoxContextMenuState | null) => void
  linkedDocsNavigationOptions?: Omit<
    LinkedDocumentsNavigationOptions,
    'offPageConnectorDocuments' | 'isLoadingOffPageConnector'
  >
  fragmentHighlight?: FragmentHighlight
  regionHighlight?: RegionHighlight
  initialIntraSearchState?: IntraDocSearchUiState
  onIntraSearchStateChange?: (state: IntraDocSearchUiState) => void
}

export interface BoxContextMenuState {
  position: { x: number; y: number } | null
  onClose: () => void
  onOpen: () => void
  onOpenInNewTab: () => void
  onAddToContext: () => void
  showNavigationOptions: boolean
  showAddToContext: boolean
  existingDashboardTabs: DashboardTabInfo[]
  onAddToExistingDashboard: (tabId: string) => void
}

export const DocumentTabContent: React.FC<DocumentTabContentProps> = ({
  documentId,
  pageNumber,
  selectedBoxIds,
  selectedSubjectUid,
  scale,
  isActiveTab,
  hasTimeSeriesData,
  getMatchedDatapoints,
  setIsChatbotOpen,
  onBoxContextMenuChange,
  linkedDocsNavigationOptions,
  fragmentHighlight,
  regionHighlight,
  initialIntraSearchState,
  onIntraSearchStateChange,
}) => {
  const {
    documentSignedURL,
    totalPages,
    documentTableRow,
    intraDocSearchDocumentId,
    sourceDocumentIdPresent,
    isSelectedDocumentUnavailable,
    docReferenceSubjects,
    documentSubjects,
  } = useDocumentTabDataContext()
  const instrumentSelection = useInstrumentSelectionOptional()
  const { siteID, showDocumentOcrBoxes } = useKnowledgeDocumentDataContext()
  const { errorNotification, infoNotification } = useGeckoNotifications()
  const { trackEvent } = useGeckoAnalytics()
  const forceHideDocumentBoxes =
    shouldForceHideDocumentBoxes(showDocumentOcrBoxes)
  const { user } = useGeckoPermissions()
  useAddDocumentToRecents(documentId)
  const userId = user?.id

  const pdfUrl = documentSignedURL?.signed_url ?? ''

  const intraSearch = useIntraDocumentSearch(intraDocSearchDocumentId, siteID, {
    initialState: initialIntraSearchState,
    onStateChange: onIntraSearchStateChange,
  })
  const fragment = useFragmentHighlight(
    documentId,
    siteID,
    fragmentHighlight,
    intraSearch.isOpen
  )

  const {
    activeTabId,
    tooltipOptions,
    setTooltipOptions,
    viewLayout,
    actionMode,
    isFullscreen,
    isSidebarCollapsed,
    setIsSidebarCollapsed,
    filterOptions,
    setFilterOptions,
    updateDocumentHistoryItem,
    pushToHistory,
    recordViewerAction,
  } = useKnowledgeDocumentUXContext()

  const [hoveredOffPageTexts, setHoveredOffPageTexts] = useState<
    string[] | undefined
  >()

  const {
    systemDocumentEntries: offPageConnectorDocuments,
    isLoading: isLoadingOffPageConnector,
  } = useOffPageConnectorDocuments(hoveredOffPageTexts, siteID)

  // A clicked box's subject: its canonical-tag alias resolves where the box's
  // own text (an OPC flag's descriptive label) cannot.
  const findDocReferenceSubject = useCallback(
    (highlight: AliasHighlight) =>
      docReferenceSubjects.find(
        (subject) => subject.subject_uid === highlight.subjectUid
      ),
    [docReferenceSubjects]
  )

  const { navigateToLinkedDocuments, showLinkedDocsTable } =
    useLinkedDocumentsNavigation({
      offPageConnectorDocuments,
      isLoadingOffPageConnector,
      ...linkedDocsNavigationOptions,
    })

  const handleBoxHover = useCallback((box: Box | null) => {
    setHoveredOffPageTexts(
      box?.type === DocumentViewerAssetType.OFF_PAGE_CONNECTOR && box.label
        ? [box.label]
        : undefined
    )
  }, [])

  // Hovering a doc-reference box primes the same string-matched document
  // lookup the legacy OPC path uses, so the click below resolves instantly.
  const handleDocReferenceHover = useCallback(
    (highlight: AliasHighlight | null) => {
      setHoveredOffPageTexts(
        highlight
          ? docReferenceResolutionTexts(
              findDocReferenceSubject(highlight),
              highlight.label
            )
          : undefined
      )
    },
    [findDocReferenceSubject]
  )

  const handleDocReferenceClick = useCallback(
    (highlight: AliasHighlight) => {
      if (isLoadingOffPageConnector) return
      const resolutionTexts = docReferenceResolutionTexts(
        findDocReferenceSubject(highlight),
        highlight.label
      )
      let referenceText = normalizeReferenceText(highlight.label)
      let targets: NavigationTargetCandidate[] = []
      for (const text of resolutionTexts) {
        targets = resolveDocReferenceTargets({
          referenceText: text,
          currentDocumentId: documentId,
          systemDocumentEntries: offPageConnectorDocuments,
        })
        if (targets.length > 0) {
          referenceText = text
          break
        }
      }
      if (targets.length === 0) {
        // The resolver excludes the current document, so a sheet's own
        // title-block print resolves to nothing — acknowledge the identity
        // instead of reporting a failed lookup.
        const selfText = resolutionTexts.find((text) =>
          isSelfReference(text, documentTableRow?.drawing)
        )
        if (selfText) {
          infoNotification(
            `"${selfText}" is this document's own drawing number`
          )
          return
        }
        errorNotification(`No document found for reference "${referenceText}"`)
        return
      }

      const jumpTo = (target: NavigationTargetCandidate) => {
        recordViewerAction(
          `Followed reference "${referenceText}" to ${target.documentId}`
        )
        pushToHistory(activeTabId, {
          type: 'document',
          documentId: target.documentId,
          pageNumber: target.pageNumber,
          selectedBoxIds: [],
          query: '',
          referenceOrigin: {
            documentId,
            label:
              documentTableRow?.drawing ??
              documentTableRow?.title ??
              documentId,
          },
        })
      }

      if (targets.length === 1) {
        jumpTo(targets[0])
        return
      }
      if (allCandidatesHaveRevisionCodes(targets)) {
        const latest = getLatestCandidateByRevision(targets)
        if (latest) {
          jumpTo(latest)
          return
        }
      }
      showLinkedDocsTable(
        targets.map((target) => target.documentId),
        referenceText,
        false
      )
    },
    [
      isLoadingOffPageConnector,
      findDocReferenceSubject,
      documentId,
      offPageConnectorDocuments,
      errorNotification,
      infoNotification,
      recordViewerAction,
      pushToHistory,
      activeTabId,
      documentTableRow,
      showLinkedDocsTable,
    ]
  )

  const effectiveLayout = totalPages <= 1 ? 'single' : viewLayout

  useLayoutEffect(() => {
    if (totalPages > 1 && effectiveLayout === 'twoPage') {
      setTooltipOptions((prev) => {
        if (prev.includes(TooltipOption.ShowMultiplePages)) return prev
        return [
          ...prev,
          TooltipOption.ShowMultiplePages,
          TooltipOption.MultiPageTurner,
        ]
      })
    }
  }, [totalPages, effectiveLayout, setTooltipOptions])

  const viewerRef = useRef<DocumentViewerHandle>(null)

  const clearSelectedSubject = useCallback(
    () =>
      updateDocumentHistoryItem(activeTabId, { selectedSubjectUid: undefined }),
    [activeTabId, updateDocumentHistoryItem]
  )

  const {
    boxContextMenuPosition,
    onBoxClick,
    onBoxAuxClick,
    onBoxContextMenu,
    handleBoxContextMenuClose,
    handleBoxContextMenuOpen,
    handleBoxContextMenuOpenInNewTab,
    handleBoxContextMenuAddToContext,
    contextMenuBoxHasTimeSeriesData,
    contextMenuBoxIsOffPageConnector,
    existingDashboardTabs,
    handleBoxContextMenuAddToExistingDashboard,
    onMultipleBoxSelect,
    handlePageClick,
  } = useBoxClickHandler({
    hasTimeSeriesData,
    getMatchedDatapoints,
    navigateToLinkedDocuments,
    setIsChatbotOpen,
  })

  const prevMenuPosRef = useRef(boxContextMenuPosition)
  useEffect(() => {
    if (boxContextMenuPosition !== prevMenuPosRef.current) {
      prevMenuPosRef.current = boxContextMenuPosition
      if (boxContextMenuPosition) {
        onBoxContextMenuChange({
          position: boxContextMenuPosition,
          onClose: handleBoxContextMenuClose,
          onOpen: handleBoxContextMenuOpen,
          onOpenInNewTab: handleBoxContextMenuOpenInNewTab,
          onAddToContext: handleBoxContextMenuAddToContext,
          showNavigationOptions: contextMenuBoxIsOffPageConnector,
          showAddToContext: contextMenuBoxHasTimeSeriesData,
          existingDashboardTabs,
          onAddToExistingDashboard: handleBoxContextMenuAddToExistingDashboard,
        })
      } else {
        onBoxContextMenuChange(null)
      }
    }
  })

  const multiPageTurner = tooltipOptions.includes(TooltipOption.MultiPageTurner)

  const handlePageChange = useCallback(
    (newPage: number) => {
      recordViewerAction(`Navigated to page ${newPage}`)
      updateDocumentHistoryItem(activeTabId, {
        pageNumber: newPage,
        selectedBoxIds: [],
      })
    },
    [activeTabId, recordViewerAction, updateDocumentHistoryItem]
  )

  usePageNavigation({
    currentPageNumber: pageNumber,
    totalPages,
    onPageChange: handlePageChange,
    pageStep: effectiveLayout === 'twoPage' && multiPageTurner ? 2 : 1,
    enabled: isActiveTab,
    viewLayout: effectiveLayout,
  })

  const handleZoomToBox = useCallback(
    (id: string) => {
      viewerRef.current?.zoomToBox(id, 2.5)
    },
    [viewerRef]
  )

  const showMultiplePages =
    effectiveLayout === 'twoPage' &&
    tooltipOptions.includes(TooltipOption.ShowMultiplePages)

  const handleResetZoom = useCallback(() => {
    viewerRef.current?.resetZoom()
  }, [])

  // A cited-region highlight rides the same machinery as search/fragment
  // hits: synthesize one hit from the normalized boxes so the overlay and
  // auto-pan paths need no special-casing (and no OCR block ids).
  const regionHit = useMemo<IntraDocSearchHit | null>(
    () =>
      regionHighlight && regionHighlight.boxes.length > 0
        ? {
            page_number: regionHighlight.pageNum,
            matched_text: '',
            bounding_boxes: regionHighlight.boxes,
          }
        : null,
    [regionHighlight]
  )

  const allHits = useMemo(() => {
    if (intraSearch.isOpen) return intraSearch.hits
    if (regionHit) return [regionHit]
    if (fragment.isActive && fragment.hit) return [fragment.hit]
    return []
  }, [
    intraSearch.isOpen,
    intraSearch.hits,
    regionHit,
    fragment.isActive,
    fragment.hit,
  ])

  const visiblePageHits = useMemo(
    () =>
      allHits.filter(
        (h) =>
          h.page_number === pageNumber ||
          (showMultiplePages && h.page_number === pageNumber + 1)
      ),
    [allHits, pageNumber, showMultiplePages]
  )

  const activeVisibleIndex = useMemo(() => {
    if (!intraSearch.isOpen || intraSearch.activeHitIndex < 0) return null
    const activeHit = intraSearch.hits[intraSearch.activeHitIndex]
    if (!activeHit) return null
    const idx = visiblePageHits.findIndex((h) => h === activeHit)
    return idx >= 0 ? idx : null
  }, [
    intraSearch.isOpen,
    intraSearch.activeHitIndex,
    intraSearch.hits,
    visiblePageHits,
  ])

  const searchHighlights: SearchHighlightData | undefined = useMemo(() => {
    if (visiblePageHits.length === 0) return undefined
    return {
      matches: visiblePageHits.map((h, i) => ({
        id: i,
        bboxes: h.bounding_boxes,
        isRightPage: showMultiplePages && h.page_number === pageNumber + 1,
      })),
      activeMatchId: activeVisibleIndex,
    }
  }, [visiblePageHits, activeVisibleIndex, showMultiplePages, pageNumber])

  const openSearchRef = useRef(intraSearch.open)
  openSearchRef.current = intraSearch.open

  const fragmentHighlightRef = useRef(fragmentHighlight)
  fragmentHighlightRef.current = fragmentHighlight
  const regionHighlightRef = useRef(regionHighlight)
  regionHighlightRef.current = regionHighlight

  useEffect(() => {
    if (
      intraSearch.isOpen &&
      (fragmentHighlightRef.current || regionHighlightRef.current)
    ) {
      updateDocumentHistoryItem(activeTabId, {
        fragmentHighlight: undefined,
        regionHighlight: undefined,
      })
    }
  }, [intraSearch.isOpen, activeTabId, updateDocumentHistoryItem])

  // Clear a cited-region highlight once the user navigates off its page —
  // same lifecycle as the fragment highlight below. Gated on the viewer
  // having actually framed the region (onFrameApplied below): pageNumber
  // alone can't distinguish "user left the page" from the transients of
  // getting there — on mount, and mid-glide in continuous scroll, where
  // intermediate pages are reported while the viewport is still traveling —
  // and clearing on those would kill the highlight before it ever showed.
  const regionFramedRef = useRef<IntraDocSearchHit | null>(null)
  const handleRegionFramed = useCallback(() => {
    regionFramedRef.current = regionHit
  }, [regionHit])
  useEffect(() => {
    if (!regionHit || regionFramedRef.current !== regionHit) return
    const isVisible =
      regionHit.page_number === pageNumber ||
      (showMultiplePages && regionHit.page_number === pageNumber + 1)
    if (!isVisible) {
      updateDocumentHistoryItem(activeTabId, {
        regionHighlight: undefined,
      })
    }
  }, [
    regionHit,
    pageNumber,
    showMultiplePages,
    activeTabId,
    updateDocumentHistoryItem,
  ])

  useEffect(() => {
    if (!fragmentHighlightRef.current || !fragment.isActive) return
    const fragPage = fragmentHighlightRef.current.pageNum
    const isVisible =
      fragPage === pageNumber ||
      (showMultiplePages && fragPage === pageNumber + 1)
    if (!isVisible) {
      updateDocumentHistoryItem(activeTabId, {
        fragmentHighlight: undefined,
      })
    }
  }, [
    pageNumber,
    showMultiplePages,
    fragment.isActive,
    activeTabId,
    updateDocumentHistoryItem,
  ])

  const lastNavigatedHitRef = useRef<typeof intraSearch.activeHit>(null)
  const restoreNavQueryRef = useRef(
    initialIntraSearchState?.isOpen
      ? initialIntraSearchState.query || null
      : null
  )
  const pageNumberRef = useRef(pageNumber)
  pageNumberRef.current = pageNumber
  const showMultiplePagesRef = useRef(showMultiplePages)
  showMultiplePagesRef.current = showMultiplePages

  const [persistedThumbPrefs] = useState(() =>
    userId ? getViewerPreferences(userId) : null
  )
  const [isThumbnailSidebarOpen, setIsThumbnailSidebarOpen] = useState(
    persistedThumbPrefs?.isThumbnailSidebarOpen ?? true
  )
  const [thumbnailSidebarWidth, setThumbnailSidebarWidth] = useState<
    number | null
  >(persistedThumbPrefs?.thumbnailSidebarWidth ?? null)

  const isMultiPage = totalPages > 1
  const effectiveThumbnailSidebarOpen = isMultiPage && isThumbnailSidebarOpen

  const handleToggleThumbnailSidebar = useCallback(() => {
    if (!isMultiPage) return
    setIsThumbnailSidebarOpen((prev) => !prev)
  }, [isMultiPage])

  const handleThumbnailPageSelect = useCallback(
    (newPage: number) => {
      recordViewerAction(`Selected thumbnail for page ${newPage}`)
      updateDocumentHistoryItem(activeTabId, {
        pageNumber: newPage,
        selectedBoxIds: [],
      })
    },
    [activeTabId, recordViewerAction, updateDocumentHistoryItem]
  )

  const handleThumbnailResizeEnd = useCallback(() => {
    handleResetZoom()
  }, [handleResetZoom])

  const previousFullscreenState = useRef(isFullscreen)
  const fullscreenResetTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    if (previousFullscreenState.current === isFullscreen) {
      return
    }

    previousFullscreenState.current = isFullscreen
    if (fullscreenResetTimeoutRef.current) {
      clearTimeout(fullscreenResetTimeoutRef.current)
    }

    fullscreenResetTimeoutRef.current = setTimeout(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          handleResetZoom()
        })
      })
    }, 100)

    return () => {
      if (fullscreenResetTimeoutRef.current) {
        clearTimeout(fullscreenResetTimeoutRef.current)
      }
    }
  }, [isFullscreen, handleResetZoom])

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        isActiveTab &&
        (event.ctrlKey || event.metaKey) &&
        event.key === 'f'
      ) {
        event.preventDefault()
        openSearchRef.current()
        return
      }

      const activeElement = document.activeElement
      if (
        activeElement instanceof HTMLInputElement ||
        activeElement instanceof HTMLTextAreaElement
      ) {
        return
      }

      if (event.key === 'Escape') {
        if (fragment.isActive || regionHighlightRef.current) {
          event.preventDefault()
          updateDocumentHistoryItem(activeTabId, {
            fragmentHighlight: undefined,
            regionHighlight: undefined,
          })
        } else if (selectedBoxIds.length > 0) {
          event.preventDefault()
          updateDocumentHistoryItem(activeTabId, { selectedBoxIds: [] })
        }
      } else if (event.key === 'b' || event.key === 'B') {
        event.preventDefault()
        if (forceHideDocumentBoxes) return

        const newFilter =
          filterOptions === FilterOption.ShowConfirmed
            ? FilterOption.RemoveBoxes
            : FilterOption.ShowConfirmed
        recordViewerAction(`Changed box filter to ${newFilter}`)
        setFilterOptions(newFilter)
      } else if (event.key === 't' || event.key === 'T') {
        event.preventDefault()
        const showingTooltips = tooltipOptions.includes(
          TooltipOption.ShowTooltips
        )
        const boxesHidden = filterOptions === FilterOption.RemoveBoxes
        if (!boxesHidden) {
          const newOptions = showingTooltips
            ? tooltipOptions.filter((opt) => opt !== TooltipOption.ShowTooltips)
            : [...tooltipOptions, TooltipOption.ShowTooltips]
          recordViewerAction(
            showingTooltips
              ? 'Hid document tooltips'
              : 'Showed document tooltips'
          )
          setTooltipOptions(newOptions)
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [
    isActiveTab,
    activeTabId,
    fragment.isActive,
    filterOptions,
    forceHideDocumentBoxes,
    selectedBoxIds.length,
    setFilterOptions,
    tooltipOptions,
    setTooltipOptions,
    recordViewerAction,
    updateDocumentHistoryItem,
  ])

  const navigateToHit = useCallback(
    (hit: IntraDocSearchHit) => {
      if (hit === lastNavigatedHitRef.current) return
      lastNavigatedHitRef.current = hit

      const currentPage = pageNumberRef.current
      const isMultiPage = showMultiplePagesRef.current
      const alreadyVisible =
        hit.page_number === currentPage ||
        (isMultiPage && hit.page_number === currentPage + 1)

      if (!alreadyVisible) {
        recordViewerAction(
          `Navigated to search match on page ${hit.page_number}`
        )
        updateDocumentHistoryItem(activeTabId, {
          pageNumber: hit.page_number,
          selectedBoxIds: [],
        })
      }

      if (!isMultiPage && hit.bounding_boxes.length > 0) {
        requestAnimationFrame(() => {
          viewerRef.current?.panToRegion?.(hit.bounding_boxes[0])
        })
      }
    },
    [activeTabId, recordViewerAction, updateDocumentHistoryItem]
  )

  const handleFocusAlias = useCallback(
    (highlight: AliasHighlight, event?: React.MouseEvent) => {
      if (instrumentSelection) {
        const subject = documentSubjects.find(
          (candidate) => candidate.subject_uid === highlight.subjectUid
        )
        if (subject?.kind === 'instrument') {
          const instrument = {
            key: subject.asset_id,
            label: subject.name || highlight.label,
            sourceLabel: highlight.label,
            datapointIds: getMatchedDatapoints(highlight.label).map(
              (dp) => dp.id
            ),
            assetId: subject.asset_id,
            subjectUid: subject.subject_uid,
            documentId,
            boxIds: [],
          }
          if (event?.shiftKey) {
            instrumentSelection.toggle(instrument)
          } else {
            instrumentSelection.select(instrument)
          }
          return
        }
      }
      requestAnimationFrame(() => {
        viewerRef.current?.panToRegion?.(highlight.bbox)
      })
      // Surface the clicked subject's documents in the (expanded) sidebar.
      updateDocumentHistoryItem(activeTabId, {
        ...(highlight.page !== pageNumber && { pageNumber: highlight.page }),
        selectedSubjectUid: highlight.subjectUid,
      })
      setIsSidebarCollapsed(false)
    },
    [
      activeTabId,
      pageNumber,
      updateDocumentHistoryItem,
      setIsSidebarCollapsed,
      instrumentSelection,
      documentSubjects,
      getMatchedDatapoints,
      documentId,
    ]
  )

  const lastOpenedDocumentRef = useRef<string | null>(null)

  useEffect(() => {
    if (!documentId) return
    if (lastOpenedDocumentRef.current === documentId) return
    lastOpenedDocumentRef.current = documentId
    recordViewerAction(`Opened document ${documentId} on page ${pageNumber}`)
  }, [documentId, pageNumber, recordViewerAction])

  const searchActionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  )
  const lastRecordedSearchQueryRef = useRef('')

  useEffect(() => {
    const query = intraSearch.query.trim()
    if (
      !intraSearch.isOpen ||
      !query ||
      query === lastRecordedSearchQueryRef.current
    ) {
      return
    }

    if (searchActionTimeoutRef.current) {
      clearTimeout(searchActionTimeoutRef.current)
    }

    searchActionTimeoutRef.current = setTimeout(() => {
      lastRecordedSearchQueryRef.current = query
      recordViewerAction(`Searched within document for "${query}"`)
      trackEvent(KNOWLEDGE_DOC_INTRA_DOC_SEARCH.title, {
        system_document_entry_id: documentId,
        source_document_id_present: sourceDocumentIdPresent,
      })
    }, 800)

    return () => {
      if (searchActionTimeoutRef.current) {
        clearTimeout(searchActionTimeoutRef.current)
      }
    }
  }, [
    documentId,
    intraSearch.isOpen,
    intraSearch.query,
    recordViewerAction,
    sourceDocumentIdPresent,
    trackEvent,
  ])

  useEffect(() => {
    if (!intraSearch.activeHit || !intraSearch.isOpen) {
      lastNavigatedHitRef.current = null
      return
    }
    if (
      restoreNavQueryRef.current !== null &&
      restoreNavQueryRef.current === intraSearch.query
    ) {
      restoreNavQueryRef.current = null
      lastNavigatedHitRef.current = intraSearch.activeHit
      return
    }
    navigateToHit(intraSearch.activeHit)
  }, [
    intraSearch.activeHit,
    intraSearch.isOpen,
    intraSearch.query,
    navigateToHit,
  ])

  useEffect(() => {
    if (!fragment.hit || !fragment.isActive || intraSearch.isOpen) return
    navigateToHit(fragment.hit)
  }, [fragment.hit, fragment.isActive, intraSearch.isOpen, navigateToHit])

  // Navigate to a cited region's page if it isn't displayed yet. The
  // framing itself (center + zoom) is declarative: the frameTarget prop
  // below hands the region to the viewer, which applies it once its page
  // imagery is measured — no readiness polling here.
  const regionNavRef = useRef<IntraDocSearchHit | null>(null)
  useEffect(() => {
    if (!regionHit || intraSearch.isOpen) return
    if (regionNavRef.current === regionHit) return
    regionNavRef.current = regionHit

    const currentPage = pageNumberRef.current
    const alreadyVisible =
      regionHit.page_number === currentPage ||
      (showMultiplePagesRef.current &&
        regionHit.page_number === currentPage + 1)
    if (!alreadyVisible) {
      recordViewerAction(
        `Navigated to cited region on page ${regionHit.page_number}`
      )
      updateDocumentHistoryItem(activeTabId, {
        pageNumber: regionHit.page_number,
        selectedBoxIds: [],
      })
    }
  }, [
    regionHit,
    intraSearch.isOpen,
    activeTabId,
    recordViewerAction,
    updateDocumentHistoryItem,
  ])

  // Unlike search hits (pan-only, user owns the zoom), a citation should
  // land already framed.
  const frameTarget = useMemo<FrameTarget | undefined>(
    () =>
      regionHit && !intraSearch.isOpen
        ? {
            bbox: regionHit.bounding_boxes[0],
            pageNumber: regionHit.page_number,
          }
        : undefined,
    [regionHit, intraSearch.isOpen]
  )

  useEffect(() => {
    if (!userId || !isMultiPage) return
    const timer = setTimeout(() => {
      updateViewerPreferences(userId, {
        isThumbnailSidebarOpen,
        thumbnailSidebarWidth,
      })
    }, 300)
    return () => clearTimeout(timer)
  }, [userId, isMultiPage, isThumbnailSidebarOpen, thumbnailSidebarWidth])

  // A deep link (or persisted tab) can point at a document id that has no
  // entry in the site's currently selected pipeline-config scope. Say so
  // explicitly and leave the URL untouched so the link can be copied,
  // corrected, or reported — never silently swallow it.
  if (isSelectedDocumentUnavailable) {
    return (
      <DocumentContentRoot role='presentation'>
        <DocumentUnavailablePanel documentId={documentId} siteID={siteID} />
      </DocumentContentRoot>
    )
  }

  return (
    <DocumentContentRoot role='presentation' onClick={handlePageClick}>
      <KnowledgeDocumentHeaderBar
        isThumbnailSidebarOpen={effectiveThumbnailSidebarOpen}
        onToggleThumbnailSidebar={handleToggleThumbnailSidebar}
        isMultiPage={isMultiPage}
      />
      <ViewerAndSidebarContainer>
        <ThumbnailSidebarContainer
          $isOpen={effectiveThumbnailSidebarOpen}
          $width={thumbnailSidebarWidth}>
          <PageThumbnailSidebar
            documentId={documentId}
            totalPages={totalPages}
            currentPage={pageNumber}
            isOpen={effectiveThumbnailSidebarOpen}
            sidebarWidth={thumbnailSidebarWidth}
            pdfUrl={pdfUrl || undefined}
            onPageSelect={handleThumbnailPageSelect}
            onWidthChange={setThumbnailSidebarWidth}
            onResizeEnd={handleThumbnailResizeEnd}
            handleResetZoom={handleResetZoom}
          />
        </ThumbnailSidebarContainer>
        <ViewerContainer>
          {intraSearch.isOpen && (
            <IntraDocSearchBar searchState={intraSearch} />
          )}
          <DocumentViewer
            ref={viewerRef}
            documentId={documentId}
            isActiveTab={isActiveTab}
            pageNumber={pageNumber}
            pdfUrl={pdfUrl}
            scale={scale}
            totalPages={totalPages}
            showMultiplePages={showMultiplePages}
            viewLayout={effectiveLayout}
            searchHighlights={searchHighlights}
            frameTarget={frameTarget}
            onFrameApplied={handleRegionFramed}
            onPageChange={handlePageChange}
            highlightBoxIds={selectedBoxIds}
            onBoxClick={onBoxClick}
            onBoxAuxClick={onBoxAuxClick}
            onBoxContextMenu={onBoxContextMenu}
            onBoxHover={handleBoxHover}
            onMultipleBoxSelect={onMultipleBoxSelect}
            onAliasClick={handleFocusAlias}
            onDocReferenceClick={handleDocReferenceClick}
            onDocReferenceHover={handleDocReferenceHover}
            actionMode={actionMode}
            showTooltips={tooltipOptions.includes(TooltipOption.ShowTooltips)}
          />
        </ViewerContainer>

        <SidebarContainer $isCollapsed={isSidebarCollapsed}>
          <KnowledgeDocumentSidebar
            handleResetZoom={handleResetZoom}
            handleZoomToBox={handleZoomToBox}
            selectedSubjectUid={selectedSubjectUid}
            clearSelectedSubject={clearSelectedSubject}
          />
        </SidebarContainer>
      </ViewerAndSidebarContainer>
    </DocumentContentRoot>
  )
}
